import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import AdComponent from './AdComponent';
import '../css/NewsPage.css';

const setMetaTag = (attr, key, value) => {
  if (!value) return;
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
};

const NewsPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shortLink, setShortLink] = useState('');
  const [shortening, setShortening] = useState(false);
  const [copied, setCopied] = useState(false);
  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://urlshortener-serverside.onrender.com/api';

  let sourceHost = '';
  try {
    sourceHost = post?.url ? new URL(post.url).hostname.replace(/^www\./, '') : '';
  } catch (error) {
    sourceHost = '';
  }

  useEffect(() => {
    let isMounted = true;
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/news`);
        const data = response.data;
        const posts = Array.isArray(data) ? data : data?.posts || [];
        if (!isMounted) return;
        const found = posts.find((item) => item.slug === slug);
        if (!found) {
          setError('Post not found.');
        } else {
          setPost(found);
          setRecent(posts.filter((item) => item.slug !== slug).slice(0, 5));
        }
      } catch (err) {
        if (!isMounted) return;
        setError('Could not load the post.');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    fetchPost();
    const interval = setInterval(fetchPost, 60000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [BASE_URL, slug]);

  useEffect(() => {
    if (!post) return;
    const title = `${post.title} | BlinkURL News`;
    document.title = title;
    const description = post.excerpt || post.content || 'Latest news from BlinkURL.';
    const fallbackImage = `${window.location.origin}/applogo.png`;
    setMetaTag('name', 'description', description);
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:image', post.image || fallbackImage);
    setMetaTag('property', 'og:url', `${window.location.origin}/news/${post.slug}`);
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', post.image || fallbackImage);
  }, [post]);

  const buildShortLink = async () => {
    if (!post?.url) return '';
    if (shortLink) return shortLink;
    setShortening(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/links/shorten`,
        {
          originalUrl: post.url,
          creator: localStorage.getItem('username') || 'news',
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const shortCode = response.data?.shortUrl;
      const full = shortCode ? `${window.location.origin}/${shortCode}` : '';
      setShortLink(full);
      return full;
    } catch (err) {
      return '';
    } finally {
      setShortening(false);
    }
  };

  const handleReadSource = async () => {
    const full = await buildShortLink();
    window.open(full || post?.url, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    const full = shortLink || (await buildShortLink());
    if (!full) return;
    await navigator.clipboard.writeText(full);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  if (loading) return <div className="news-shell"><div className="news-state">Loading post...</div></div>;
  if (error || !post) return <div className="news-shell"><div className="news-state">{error || 'Post not found.'}</div></div>;

  const contentText = post.content || post.excerpt || '';
  let intro = contentText;
  let outro = '';
  if (contentText.length > 240) {
    const midpoint = Math.floor(contentText.length / 2);
    let splitIndex = contentText.indexOf('.', midpoint);
    if (splitIndex === -1) splitIndex = contentText.indexOf(' ', midpoint);
    if (splitIndex === -1) splitIndex = midpoint;
    intro = contentText.slice(0, splitIndex + 1).trim();
    outro = contentText.slice(splitIndex + 1).trim();
  }

  return (
    <div className="news-shell">
      <article className="news-post-layout">
        <main className="news-post-main">
          <Link to="/news" className="back-link">← Back to News</Link>
          <div className="chip-row">
            <span className="chip">{post.category || 'News'}</span>
            <span className="chip">{post.subcategory || 'General'}</span>
          </div>
          <h1>{post.title}</h1>
          <div className="news-post-meta">
            <span>{new Date(post.publishedAt).toLocaleString()}</span>
            {sourceHost && <span>{`Source: ${sourceHost}`}</span>}
          </div>
          <img src={post.image} alt={post.title} />
          <p>{intro}</p>
          <div className="taboola-mid">
            <AdComponent containerId={`taboola-mid-article-${post.slug}`} />
          </div>
          {outro && <p>{outro}</p>}
          <div className="source-actions">
            <button type="button" className="primary" onClick={handleReadSource} disabled={shortening}>
              {shortening ? 'Building link...' : 'Read Full Source'}
            </button>
            <button type="button" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy Short Link'}
            </button>
          </div>
          {shortLink && <small className="short-link-preview">{shortLink}</small>}
        </main>
        <aside className="news-post-side">
          <h3>Recent Posts</h3>
          {recent.map((item) => (
            <Link key={item.slug} to={`/news/${item.slug}`}>
              {item.title}
            </Link>
          ))}
        </aside>
      </article>
    </div>
  );
};

export default NewsPostPage;
